import { useEffect, useMemo } from "react";

import { AppModel, type SupportZendeskTicketModel } from "@doitintl/cmp-models";
import { getCollection } from "@doitintl/models-any";
import { type CollectionDataHook, useCollectionData } from "@doitintl/models-hooks";

import { useAuthContext } from "../../Context/AuthContext";
import { useCustomerContext } from "../../Context/CustomerContext";
import { useUserContext } from "../../Context/UserContext";
import { isDoitEmployeeEmail } from "../../utils/email";

export function useListZendeskTickets(sharedTickets: boolean): CollectionDataHook<SupportZendeskTicketModel> {
  const { customerOrPresentationModeCustomer: customer } = useCustomerContext();
  const { userModel } = useUserContext({ allowNull: false });
  const { isDoitEmployee } = useAuthContext({ mustHaveUser: true });

  const isSharedTickets = !!customer.zendeskOrganizationInfo?.sharedTickets || sharedTickets;

  useEffect(() => {
    if (sharedTickets && !customer.zendeskOrganizationInfo?.sharedTickets) {
      customer.ref.update({
        "zendeskOrganizationInfo.sharedTickets": true,
      });
    }
  }, [sharedTickets, customer.ref, customer.zendeskOrganizationInfo?.sharedTickets]);

  const customerRef = customer.ref;

  const query = useMemo(() => {
    const userCustomerId = userModel.customer?.ref?.id;
    const queryCustomerId = customerRef?.id;
    const isSameCustomer = userCustomerId === queryCustomerId;

    if (!isDoitEmployee && !isSameCustomer) {
      return undefined;
    }

    let baseQuery = getCollection(AppModel)
      .doc("support")
      .collection("tickets")
      .where("customerRef", "==", customerRef)
      .orderBy("ticket.updated_at", "desc");

    if (!isDoitEmployee && !isSharedTickets) {
      baseQuery = baseQuery.where("ticket.requester_email", "==", userModel.email);
    }

    return baseQuery;
  }, [isDoitEmployee, userModel.customer?.ref?.id, userModel.email, customerRef, isSharedTickets]);

  const [data, loading, error] = useCollectionData(query);

  const filteredData = useMemo(() => {
    if (!data) {
      return undefined;
    }

    return data.filter((ticket) => {
      if (!ticket.ticket.is_public) {
        return false;
      }

      const tags = ticket.ticket.tags;
      const requesterEmail = ticket.ticket.requester_email;

      if (tags.includes("get_doer_request")) {
        return false;
      }

      if (tags.includes("opened_on_behalf")) {
        return true;
      }

      if (isDoitEmployeeEmail(requesterEmail) && !tags.includes("opened_on_behalf")) {
        return false;
      }

      return true;
    });
  }, [data]);

  return [filteredData, loading, error] as CollectionDataHook<SupportZendeskTicketModel>;
}
